import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Search, Upload, Download, X, ArrowLeft, Save,
  Users as UsersIcon, Mail, Phone, MapPin, Shield, KeyRound, UserX,
  Building2, ShoppingCart, RotateCcw, ArrowLeftRight, ChevronRight, Eye,
  Barcode,
} from "lucide-react";
import { api, API, getToken } from "../../../lib/auth";
import { fmtMoney as fmt } from "../../../lib/currency";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, Toggle, SectionHeader } from "../products/_shared";

// ═══════════════════════════════ PEOPLE: CUSTOMER / SUPPLIER LIST ═══════════════════════════════
function PersonList({ kind, title, breadcrumb, endpoint, testidBase, createTo, importTo }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const { subdomain } = useParams();
  const nav = useNavigate();

  const load = () => api.get(endpoint, { params: search ? { q: search } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`${endpoint}/${p.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8">
      <PageHeader title={title} breadcrumb={breadcrumb}
        actions={<>
          <Link to={`/t/${subdomain}/${importTo}`}>
            <button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold hover:border-black/25"><Upload size={13} /> Import</button>
          </Link>
          <Link to={`/t/${subdomain}/${createTo}`}><PrimaryButton icon={Plus} testid={`${testidBase}-new`}>Create {kind}</PrimaryButton></Link>
        </>} />

      <Card className="p-3 mb-4">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={`Search ${kind.toLowerCase()}s…`} data-testid={`${testidBase}-search`}
            className="flex-1 bg-transparent focus:outline-none text-sm" />
          <button onClick={load} className="text-xs font-bold text-[#1FA868]">Search</button>
        </div>
      </Card>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Name</th>
              <th className="text-left p-3.5 font-bold">Email</th>
              <th className="text-left p-3.5 font-bold">Phone</th>
              <th className="text-left p-3.5 font-bold">City</th>
              <th className="text-left p-3.5 font-bold">Status</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#E8F5E9] text-[#1FA868] font-bold text-xs flex items-center justify-center">
                      {(p.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-black">{p.name}</div>
                      {p.company && <div className="text-[11px] text-black/45">{p.company}</div>}
                    </div>
                  </div>
                </td>
                <td className="p-3.5 text-black/70">{p.email || "—"}</td>
                <td className="p-3.5 text-black/70">{p.phone || "—"}</td>
                <td className="p-3.5 text-black/70">{p.city || "—"}</td>
                <td className="p-3.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${p.active ? "bg-[#E8F5E9] text-[#1FA868]" : "bg-black/5 text-black/55"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-3.5 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => nav(`/t/${subdomain}/${createTo.replace("/new", "")}/${p.id}/edit`)}
                      data-testid={`${testidBase}-edit-${p.id}`}
                      className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D1F0DA]"><Pencil size={13} /></button>
                    <button onClick={() => remove(p)} data-testid={`${testidBase}-delete-${p.id}`}
                      className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-black/45">No {kind.toLowerCase()}s.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PersonForm({ kind, endpoint, testidBase, listTo, hasCompany = false }) {
  const { subdomain, personId } = useParams();
  const nav = useNavigate();
  const isEdit = !!personId;
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", company: "", notes: "", active: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isEdit) api.get(endpoint).then((r) => {
      const found = r.data.find((x) => x.id === personId);
      if (found) setForm({ ...form, ...found });
      else { toast.error("Not found"); nav(`/t/${subdomain}/${listTo}`); }
    });
  }, [personId]); // eslint-disable-line

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      if (isEdit) await api.patch(`${endpoint}/${personId}`, form);
      else await api.post(endpoint, form);
      toast.success("Saved");
      nav(`/t/${subdomain}/${listTo}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testidBase}-form`} className="p-6 lg:p-8 max-w-3xl">
      <Link to={`/t/${subdomain}/${listTo}`} className="inline-flex items-center gap-1.5 text-sm text-black/55 hover:text-black mb-2"><ArrowLeft size={13} /> Back</Link>
      <h1 className="font-display font-extrabold text-3xl text-black mb-6">{isEdit ? `Edit ${kind}` : `Create ${kind}`}</h1>

      <Card className="p-6 lg:p-7 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field lbl="Name" required><TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid={`${testidBase}-name`} /></Field>
          <Field lbl="Email" hint="Used for invoices and notifications"><TextInput type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid={`${testidBase}-email`} /></Field>
          <Field lbl="Phone"><TextInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid={`${testidBase}-phone`} /></Field>
          <Field lbl="City"><TextInput value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid={`${testidBase}-city`} /></Field>
          {hasCompany && <Field lbl="Company" className="md:col-span-2"><TextInput value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid={`${testidBase}-company`} /></Field>}
          <Field lbl="Address" className="md:col-span-2"><TextInput value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid={`${testidBase}-address`} /></Field>
          <Field lbl="Notes" className="md:col-span-2">
            <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              data-testid={`${testidBase}-notes`} className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
          </Field>
          <Field lbl="Status">
            <Toggle value={form.active} onChange={(v) => setForm({ ...form, active: v })} label={form.active ? "Active" : "Inactive"} testid={`${testidBase}-active`} />
          </Field>
        </div>
        <div className="pt-4 border-t border-black/8 flex justify-end gap-2">
          <Link to={`/t/${subdomain}/${listTo}`}><button className="px-4 py-2.5 rounded-xl border border-black/15 text-sm font-semibold">Cancel</button></Link>
          <PrimaryButton onClick={submit} disabled={busy} icon={Save} testid={`${testidBase}-submit`}>{busy ? "Saving…" : "Save"}</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}

function PersonImport({ kind, endpoint, testidBase, backTo }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const { subdomain } = useParams();

  const submit = async () => {
    if (!file) return toast.error("Pick a CSV");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}${endpoint}`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const j = await r.json();
      setResult(j);
      toast.success(`Imported ${j.created}, skipped ${j.skipped}`);
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const cols = ["name", "email", "phone", "address", "city", kind === "Supplier" ? "company" : ""].filter(Boolean).join(",");
    const example = kind === "Supplier"
      ? "Global Coffee,sales@globalcoffee.test,+234 803 111 2222,Plot 15 Ikeja,Lagos,Global Coffee"
      : "John Doe,john@example.com,+234 803 555 0001,15 Main St,Lagos";
    const csv = `${cols}\n${example}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${kind.toLowerCase()}s_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8 max-w-3xl">
      <PageHeader title={`Import ${kind}s`} breadcrumb={`People · Import ${kind}s`}
        actions={<button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold hover:border-black/25" data-testid={`${testidBase}-template`}>
          <Download size={13} /> Template
        </button>} />
      <Card className="p-7">
        <div className="rounded-xl border-2 border-dashed border-black/15 p-10 text-center hover:border-[#1FA868] hover:bg-[#F0FAF3]">
          <Upload size={36} className="mx-auto text-black/30" />
          <div className="mt-3 font-semibold text-black">{file ? file.name : "Drop CSV or click to browse"}</div>
          <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files?.[0]); setResult(null); }}
            data-testid={`${testidBase}-file`} className="mt-4 mx-auto text-sm" />
        </div>
        <div className="mt-5"><PrimaryButton onClick={submit} disabled={busy || !file} icon={Upload} testid={`${testidBase}-submit`}>{busy ? "…" : "Import"}</PrimaryButton></div>
        {result && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#E8F5E9] p-4 text-center"><div className="text-[10px] uppercase text-[#1FA868] font-bold">Created</div><div className="font-display font-extrabold text-2xl text-[#1FA868]">{result.created}</div></div>
            <div className="rounded-xl bg-amber-50 p-4 text-center"><div className="text-[10px] uppercase text-amber-700 font-bold">Skipped</div><div className="font-display font-extrabold text-2xl text-amber-700">{result.skipped}</div></div>
          </div>
        )}
      </Card>
    </div>
  );
}

export const Customers = () => <PersonList kind="Customer" title="Customers" breadcrumb="People · Customers"
  endpoint="/t/customers" testidBase="customers-p" createTo="people/customers/new" importTo="people/customers/import" />;
export const CustomerForm = () => <PersonForm kind="Customer" endpoint="/t/customers" testidBase="cust-form" listTo="people/customers" />;
export const CustomerImport = () => <PersonImport kind="Customer" endpoint="/t/customers/import" testidBase="cust-import" />;

export const Suppliers = () => <PersonList kind="Supplier" title="Suppliers" breadcrumb="People · Suppliers"
  endpoint="/t/suppliers" testidBase="suppliers-p" createTo="suppliers/new" importTo="suppliers/import" />;
export const SupplierForm = () => <PersonForm kind="Supplier" endpoint="/t/suppliers" testidBase="sup-form" listTo="suppliers" hasCompany />;
export const SupplierImport = () => <PersonImport kind="Supplier" endpoint="/t/suppliers/import" testidBase="sup-import" />;

// ═══════════════════════════════ USER MANAGEMENT ═══════════════════════════════
export function UsersPage() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");

  const load = useCallback(() => {
    api.get("/t/staff").then((r) => setRows(r.data));
  }, []);
  
  useEffect(() => {
    load();
    api.get("/t/roles").then((r) => setRoles(r.data));
  }, [load]);

  const save = async () => {
    try {
      if (dlg._editing) {
        const patch = { first_name: dlg.first_name, last_name: dlg.last_name, phone: dlg.phone, role_id: dlg.role_id, active: dlg.active };
        await api.patch(`/t/staff/${dlg._editing}`, patch);
      } else {
        await api.post("/t/staff", dlg);
      }
      toast.success("Saved"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/t/staff/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doResetPw = async () => {
    if (pw.length < 8) return toast.error("8+ chars");
    await api.post(`/t/staff/${pwFor.id}/reset-password`, { new_password: pw });
    toast.success("Password reset"); setPwFor(null); setPw("");
  };

  return (
    <div data-testid="users-page" className="p-6 lg:p-8">
      <PageHeader title="Users" breadcrumb="User Management · Users"
        actions={<PrimaryButton icon={Plus} testid="users-new" onClick={() => setDlg({ first_name: "", last_name: "", email: "", phone: "", role_id: roles[0]?.id || "", password: "", active: true })}>
          Create User
        </PrimaryButton>} />

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Name</th>
              <th className="text-left p-3.5 font-bold">Email</th>
              <th className="text-left p-3.5 font-bold">Role</th>
              <th className="text-left p-3.5 font-bold">Status</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((u) => (
              <tr key={u.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white text-xs font-bold flex items-center justify-center">
                      {(u.name || u.email || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-black">{u.name}</div>
                      <div className="text-[11px] text-black/45">{u.phone || ""}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3.5 text-black/70">{u.email}</td>
                <td className="p-3.5">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold"><Shield size={11} className="text-[#1FA868]" />{u.role_name || u.role || "—"}</span>
                  {u.is_tenant_admin && <span className="ml-2 text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Owner</span>}
                </td>
                <td className="p-3.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${u.is_active ? "bg-[#E8F5E9] text-[#1FA868]" : "bg-rose-50 text-rose-600"}`}>
                    {u.is_active ? "Active" : "Blocked"}
                  </span>
                </td>
                <td className="p-3.5 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setPwFor(u)} data-testid={`users-pw-${u.id}`}
                      className="p-2 rounded-md bg-violet-50 text-violet-600 hover:bg-violet-100"><KeyRound size={13} /></button>
                    <button onClick={() => setDlg({ ...u, _editing: u.id, active: u.is_active })} data-testid={`users-edit-${u.id}`}
                      className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D1F0DA]"><Pencil size={13} /></button>
                    {!u.is_tenant_admin && <button onClick={() => remove(u)} data-testid={`users-del-${u.id}`}
                      className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {dlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDlg(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">{dlg._editing ? "Edit User" : "New User"}</h3>
              <button onClick={() => setDlg(null)} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field lbl="First name"><TextInput value={dlg.first_name} onChange={(v) => setDlg({ ...dlg, first_name: v })} testid="u-fname" /></Field>
                <Field lbl="Last name"><TextInput value={dlg.last_name} onChange={(v) => setDlg({ ...dlg, last_name: v })} testid="u-lname" /></Field>
              </div>
              <Field lbl="Email" required><TextInput type="email" value={dlg.email} onChange={(v) => setDlg({ ...dlg, email: v })} testid="u-email" disabled={dlg._editing} /></Field>
              <Field lbl="Phone"><TextInput value={dlg.phone} onChange={(v) => setDlg({ ...dlg, phone: v })} testid="u-phone" /></Field>
              <Field lbl="Role">
                <Select value={dlg.role_id} onChange={(v) => setDlg({ ...dlg, role_id: v })}
                  options={roles.map((r) => ({ value: r.id, label: r.name }))} testid="u-role" />
              </Field>
              {!dlg._editing && <Field lbl="Password" required hint="Min 8 chars"><TextInput type="password" value={dlg.password} onChange={(v) => setDlg({ ...dlg, password: v })} testid="u-password" /></Field>}
              <Field lbl="Status"><Toggle value={dlg.active} onChange={(v) => setDlg({ ...dlg, active: v })} testid="u-active" /></Field>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setDlg(null)} className="px-4 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
              <PrimaryButton onClick={save} testid="u-save">{dlg._editing ? "Save" : "Create"}</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {pwFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPwFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg">Reset password</h3>
            <p className="text-xs text-black/55 mt-1">For {pwFor.email}</p>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" autoFocus
              data-testid="u-new-pw" className="mt-3 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPwFor(null)} className="px-3 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
              <PrimaryButton onClick={doResetPw} testid="u-pw-save">Reset</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function RolesPage() {
  const [rows, setRows] = useState([]);
  const [perms, setPerms] = useState({ permissions: [], groups: {} });
  const [dlg, setDlg] = useState(null);

  const load = useCallback(() => {
    api.get("/t/roles").then((r) => setRows(r.data));
  }, []);
  
  useEffect(() => {
    load();
    api.get("/t/permissions-catalog").then((r) => setPerms(r.data));
  }, [load]);

  const toggle = (p) => setDlg((d) => ({ ...d, permissions: d.permissions.includes(p) ? d.permissions.filter((x) => x !== p) : [...d.permissions, p] }));
  const toggleGroup = (keys) => setDlg((d) => {
    const all = keys.every((k) => d.permissions.includes(k));
    return { ...d, permissions: all ? d.permissions.filter((p) => !keys.includes(p)) : [...new Set([...d.permissions, ...keys])] };
  });

  const save = async () => {
    if (!dlg.name?.trim()) return toast.error("Name required");
    try {
      if (dlg._editing) await api.patch(`/t/roles/${dlg._editing}`, { name: dlg.name, description: dlg.description, permissions: dlg.permissions, active: dlg.active !== false });
      else await api.post("/t/roles", { name: dlg.name, description: dlg.description, permissions: dlg.permissions, active: dlg.active !== false });
      toast.success("Saved"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    await api.delete(`/t/roles/${r.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="roles-page" className="p-6 lg:p-8">
      <PageHeader title="Group Permissions" breadcrumb="User Management · Group Permissions"
        actions={<PrimaryButton icon={Plus} testid="roles-new" onClick={() => setDlg({ name: "", description: "", permissions: [], active: true })}>New Role</PrimaryButton>} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => (
          <Card key={r.id} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg text-black">{r.name}</h3>
                <p className="text-xs text-black/55 mt-1 line-clamp-2">{r.description}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white flex items-center justify-center"><Shield size={16} /></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {(r.permissions || []).slice(0, 6).map((p) => (
                <span key={p} className="text-[10px] font-semibold bg-[#F0FAF3] text-[#1FA868] px-1.5 py-0.5 rounded">{p.split(".")[0]}</span>
              ))}
              {r.permissions?.length > 6 && <span className="text-[10px] font-bold text-black/55">+{r.permissions.length - 6}</span>}
            </div>
            <div className="mt-4 pt-3 border-t border-black/5 flex justify-between items-center">
              <span className="text-[11px] text-black/55">{r.permissions?.length || 0} permission(s)</span>
              <div className="flex gap-1">
                <button onClick={() => setDlg({ ...r, _editing: r.id })} data-testid={`roles-edit-${r.id}`}
                  className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868]"><Pencil size={13} /></button>
                <button onClick={() => remove(r)} data-testid={`roles-del-${r.id}`}
                  className="p-2 rounded-md bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <div className="col-span-full text-center text-black/45 py-10">No roles. Create the first one.</div>}
      </div>

      {dlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDlg(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">{dlg._editing ? "Edit Role" : "New Role"}</h3>
              <button onClick={() => setDlg(null)} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field lbl="Role name" required><TextInput value={dlg.name} onChange={(v) => setDlg({ ...dlg, name: v })} testid="r-name" /></Field>
              <Field lbl="Description">
                <textarea value={dlg.description || ""} onChange={(e) => setDlg({ ...dlg, description: e.target.value })} rows={2}
                  data-testid="r-desc" className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
              </Field>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-black/55 mb-2">Permissions</div>
                <div className="space-y-3">
                  {Object.entries(perms.groups).map(([group, keys]) => {
                    const all = keys.every((k) => dlg.permissions.includes(k));
                    const some = keys.some((k) => dlg.permissions.includes(k));
                    return (
                      <div key={group} className="rounded-xl border border-black/10 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-sm capitalize">{group}</div>
                          <button type="button" onClick={() => toggleGroup(keys)} data-testid={`r-group-${group}`}
                            className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${all ? "bg-[#1FA868] text-white" : some ? "bg-amber-100 text-amber-700" : "bg-black/5 text-black/55"}`}>
                            {all ? "All" : some ? "Some" : "None"}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {keys.map((p) => {
                            const on = dlg.permissions.includes(p);
                            return (
                              <button key={p} type="button" onClick={() => toggle(p)} data-testid={`r-perm-${p}`}
                                className={`text-xs px-2.5 py-1 rounded-full font-semibold ${on ? "bg-[#1FA868] text-white" : "bg-black/5 text-black/55 hover:bg-black/10"}`}>
                                {p.split(".")[1]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setDlg(null)} className="px-4 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
              <PrimaryButton onClick={save} testid="r-save">{dlg._editing ? "Save" : "Create"}</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════ PURCHASES ═══════════════════════════════
export function AllPurchases() {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    const params = {};
    if (search) params.q = search;
    if (status !== "all") params.status = status;
    const r = await api.get("/t/purchases", { params });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.reference}?`)) return;
    await api.delete(`/t/purchases/${p.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="purchases-page" className="p-6 lg:p-8">
      <PageHeader title="All Purchases" breadcrumb="Purchases · All Purchases"
        actions={<>
          <Link to={`/t/${subdomain}/purchases/import`}><button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><Upload size={13} /> Import</button></Link>
          <Link to={`/t/${subdomain}/purchases/new`}><PrimaryButton icon={Plus} testid="purchases-new">Create Purchase</PrimaryButton></Link>
        </>} />

      <Card className="p-3 mb-4 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2 flex-1 min-w-[260px]">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by ref or supplier…" data-testid="purchases-search"
            className="flex-1 bg-transparent focus:outline-none text-sm" />
          <button onClick={load} className="text-xs font-bold text-[#1FA868]">Search</button>
        </div>
        {["all", "received", "ordered", "pending"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`purchases-status-${s}`}
            className={`px-3 py-2 rounded-lg text-xs font-semibold capitalize ${status === s ? "bg-[#1FA868] text-white" : "bg-white border border-black/10"}`}>{s}</button>
        ))}
      </Card>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Reference</th>
              <th className="text-left p-3.5 font-bold">Date</th>
              <th className="text-left p-3.5 font-bold">Supplier</th>
              <th className="text-left p-3.5 font-bold">Items</th>
              <th className="text-left p-3.5 font-bold">Total</th>
              <th className="text-left p-3.5 font-bold">Paid</th>
              <th className="text-left p-3.5 font-bold">Status</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-mono text-xs font-bold">{p.reference}</td>
                <td className="p-3.5 text-black/65 text-xs">{p.date}</td>
                <td className="p-3.5 font-semibold">{p.supplier_name}</td>
                <td className="p-3.5 text-black/65">{(p.items || []).length}</td>
                <td className="p-3.5 font-bold tabular-nums">{fmt(p.total)}</td>
                <td className="p-3.5 text-[#1FA868] tabular-nums">{fmt(p.paid)}</td>
                <td className="p-3.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${
                    p.status === "received" ? "bg-[#E8F5E9] text-[#1FA868]" :
                    p.status === "ordered" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                </td>
                <td className="p-3.5 text-right">
                  <button onClick={() => remove(p)} data-testid={`purchase-del-${p.id}`}
                    className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-black/45">No purchases.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// Generic "Create transaction" form used for Purchase, Sales Return, Purchase Return
function TxForm({ title, breadcrumb, listTo, mode, endpoint, testidBase, contactLabel, contactEndpoint, priceField = "unit_price" }) {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [search, setSearch] = useState(""); const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    contact_id: "", contact_name: "",
    warehouse_id: "", items: [],
    order_tax: 0, order_discount: 0, shipping: 0, paid: 0,
    payment_method: "cash", status: mode === "purchase" ? "received" : undefined, note: "",
  });

  useEffect(() => {
    api.get(contactEndpoint).then((r) => {
      setContacts(r.data);
      if (r.data[0]) {
        setForm((f) => ({
          ...f,
          contact_id: r.data[0].id,
          contact_name: r.data[0].name,
        }));
      }
    });
  
    api.get("/t/warehouses").then((r) => {
      setWarehouses(r.data);
  
      if (r.data[0]) {
        setForm((f) => ({
          ...f,
          warehouse_id: r.data[0].id,
        }));
      }
    });
  
    api.get("/t/products").then((r) => setProducts(r.data));
  }, [contactEndpoint]);

  useEffect(() => {
    if (!search) return setResults([]);
    const q = search.toLowerCase();
    setResults(products.filter((p) => p.name.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)).slice(0, 6));
  }, [search, products]);

  const addItem = (p) => {
    const ex = form.items.find((i) => i.product_id === p.id);
    if (ex) setForm({ ...form, items: form.items.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i) });
    else {
      const priceVal = mode === "purchase" ? (p.cost || 0) : (p.price || 0);
      setForm({ ...form, items: [...form.items, { product_id: p.id, name: p.name, qty: 1, [priceField]: priceVal, discount: 0, tax: 0, stock: p.stock, reason: "" }] });
    }
    setSearch(""); setResults([]);
  };
  const upd = (i, patch) => { const items = [...form.items]; items[i] = { ...items[i], ...patch }; setForm({ ...form, items }); };
  const remove = (i) => setForm({ ...form, items: form.items.filter((_, k) => k !== i) });

  const subtotal = form.items.reduce((s, i) => s + (i.qty * (i[priceField] || 0) - (i.discount || 0) + (i.tax || 0)), 0);
  const tax = mode === "purchase" ? (subtotal * (form.order_tax || 0)) / 100 : 0;
  const total = mode === "purchase" ? subtotal - (form.order_discount || 0) + (form.shipping || 0) + tax : subtotal;
  const due = Math.max(0, total - (form.paid || 0));

  const submit = async () => {
    if (form.items.length === 0) return toast.error("Add items");
    if (!form.warehouse_id) return toast.error("Warehouse required");
    setBusy(true);
    try {
      const payload = { date: form.date, warehouse_id: form.warehouse_id, items: form.items.map((i) => ({
        product_id: i.product_id, name: i.name, qty: i.qty,
        [priceField]: i[priceField], discount: i.discount || 0, tax: i.tax || 0, reason: i.reason || "",
      })), note: form.note };
      if (mode === "purchase") {
        Object.assign(payload, { supplier_id: form.contact_id, supplier_name: form.contact_name,
          order_tax: form.order_tax, order_discount: form.order_discount, shipping: form.shipping,
          paid: form.paid, payment_method: form.payment_method, status: form.status });
      } else if (mode === "sales-return") {
        Object.assign(payload, { customer_id: form.contact_id, customer_name: form.contact_name });
      } else if (mode === "purchase-return") {
        Object.assign(payload, { supplier_id: form.contact_id, supplier_name: form.contact_name });
      }
      await api.post(endpoint, payload);
      toast.success("Saved"); nav(`/t/${subdomain}/${listTo}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testidBase}-form`} className="p-6 lg:p-8 pb-32">
      <Link to={`/t/${subdomain}/${listTo}`} className="inline-flex items-center gap-1.5 text-sm text-black/55 hover:text-black mb-2"><ArrowLeft size={13} /> Back</Link>
      <h1 className="font-display font-extrabold text-3xl text-black tracking-tight mb-6">{title}</h1>

      <Card className="p-6 lg:p-7 mb-5">
        <div className="grid md:grid-cols-3 gap-4">
          <Field lbl="Date" required><TextInput type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid={`${testidBase}-date`} /></Field>
          <Field lbl={contactLabel} required>
            <Select value={form.contact_id} onChange={(v) => {
              const c = contacts.find((x) => x.id === v);
              setForm({ ...form, contact_id: v, contact_name: c?.name || "" });
            }} options={contacts.map((c) => ({ value: c.id, label: c.name }))} testid={`${testidBase}-contact`} />
          </Field>
          <Field lbl="Branch" required>
            <Select value={form.warehouse_id} onChange={(v) => setForm({ ...form, warehouse_id: v })}
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid={`${testidBase}-warehouse`} />
          </Field>
        </div>
      </Card>

      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Barcode} title="Product search" />
        <div className="relative">
          <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-3 border border-black/10">
            <Barcode size={16} className="text-[#1FA868]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Scan/Search Product by Code or Name" data-testid={`${testidBase}-search`}
              className="flex-1 bg-transparent focus:outline-none text-sm" />
          </div>
          {results.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl border border-black/10 shadow-lg z-10 max-h-60 overflow-y-auto">
              {results.map((p) => (
                <button key={p.id} onClick={() => addItem(p)} data-testid={`${testidBase}-add-${p.id}`}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#F0FAF3] flex items-center justify-between border-b border-black/5">
                  <div><div className="font-semibold text-sm">{p.name}</div><div className="text-[11px] text-black/55 font-mono">{p.sku} · Stock: {p.stock}</div></div>
                  <div className="font-bold tabular-nums text-sm">{fmt(mode === "purchase" ? p.cost : p.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F0FAF3] text-[#1FA868] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-2.5 font-bold">#</th>
                <th className="text-left p-2.5 font-bold">Product</th>
                <th className="text-left p-2.5 font-bold">{mode === "purchase" ? "Unit Cost" : "Unit Price"}</th>
                <th className="text-left p-2.5 font-bold">Qty</th>
                {mode === "purchase" && <>
                  <th className="text-left p-2.5 font-bold">Discount</th>
                  <th className="text-left p-2.5 font-bold">Tax</th>
                </>}
                {mode !== "purchase" && <th className="text-left p-2.5 font-bold">Reason</th>}
                <th className="text-left p-2.5 font-bold">Subtotal</th>
                <th className="text-right p-2.5">—</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i} className="border-b border-black/5">
                  <td className="p-2.5 text-black/55">{i + 1}</td>
                  <td className="p-2.5 font-semibold">{it.name}</td>
                  <td className="p-2.5"><input type="number" value={it[priceField]} onChange={(e) => upd(i, { [priceField]: Number(e.target.value) })} className="w-24 px-2 py-1.5 rounded border border-black/15 text-xs" /></td>
                  <td className="p-2.5"><input type="number" value={it.qty} onChange={(e) => upd(i, { qty: Number(e.target.value) })} className="w-16 px-2 py-1.5 rounded border border-black/15 text-xs text-center" /></td>
                  {mode === "purchase" && <>
                    <td className="p-2.5"><input type="number" value={it.discount || 0} onChange={(e) => upd(i, { discount: Number(e.target.value) })} className="w-20 px-2 py-1.5 rounded border border-black/15 text-xs" /></td>
                    <td className="p-2.5"><input type="number" value={it.tax || 0} onChange={(e) => upd(i, { tax: Number(e.target.value) })} className="w-20 px-2 py-1.5 rounded border border-black/15 text-xs" /></td>
                  </>}
                  {mode !== "purchase" && <td className="p-2.5"><input value={it.reason || ""} onChange={(e) => upd(i, { reason: e.target.value })} placeholder="Reason" className="w-32 px-2 py-1.5 rounded border border-black/15 text-xs" /></td>}
                  <td className="p-2.5 font-bold tabular-nums">{fmt(it.qty * it[priceField] - (it.discount || 0) + (it.tax || 0))}</td>
                  <td className="p-2.5 text-right"><button onClick={() => remove(i)} className="p-1.5 rounded-md bg-rose-50 text-rose-600"><X size={12} /></button></td>
                </tr>
              ))}
              {form.items.length === 0 && <tr><td colSpan={mode === "purchase" ? 8 : 7} className="p-8 text-center text-black/45">No items yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6 lg:p-7 mb-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            {mode === "purchase" && <>
              <Field lbl="Order Tax %"><TextInput type="number" value={form.order_tax} onChange={(v) => setForm({ ...form, order_tax: v })} suffix="%" testid={`${testidBase}-tax`} /></Field>
              <Field lbl="Order Discount"><TextInput type="number" value={form.order_discount} onChange={(v) => setForm({ ...form, order_discount: v })} suffix="₦" testid={`${testidBase}-discount`} /></Field>
              <Field lbl="Shipping"><TextInput type="number" value={form.shipping} onChange={(v) => setForm({ ...form, shipping: v })} suffix="₦" testid={`${testidBase}-shipping`} /></Field>
              <Field lbl="Paid"><TextInput type="number" value={form.paid} onChange={(v) => setForm({ ...form, paid: v })} suffix="₦" testid={`${testidBase}-paid`} /></Field>
              <Field lbl="Status"><Select value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                options={[{ value: "received", label: "Received" }, { value: "ordered", label: "Ordered" }, { value: "pending", label: "Pending" }]} testid={`${testidBase}-status`} /></Field>
            </>}
            <Field lbl="Note"><textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" /></Field>
          </div>
          <div className="bg-gradient-to-br from-[#0F5132] via-[#16834D] to-[#1FA868] text-white rounded-2xl p-6 self-start">
            <div className="text-xs uppercase tracking-wider font-bold text-white/70">Summary</div>
            <div className="mt-2 space-y-2 text-sm">
              <div className="flex justify-between text-white/85"><span>Subtotal</span><span className="tabular-nums font-semibold">{fmt(subtotal)}</span></div>
              {mode === "purchase" && <>
                <div className="flex justify-between text-white/85"><span>Tax</span><span className="tabular-nums font-semibold">{fmt(tax)}</span></div>
                <div className="flex justify-between text-white/85"><span>Discount</span><span className="tabular-nums font-semibold">-{fmt(form.order_discount)}</span></div>
                <div className="flex justify-between text-white/85"><span>Shipping</span><span className="tabular-nums font-semibold">{fmt(form.shipping)}</span></div>
              </>}
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="text-white/70 text-xs uppercase tracking-wider font-bold">Grand Total</div>
              <div className="font-display font-extrabold text-4xl tracking-tight mt-1 tabular-nums">{fmt(total)}</div>
              {mode === "purchase" && (
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-white/70">Paid</div><div className="font-bold">{fmt(form.paid)}</div></div>
                  <div><div className="text-white/70">Due</div><div className="font-bold">{fmt(due)}</div></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center justify-between sticky bottom-4 shadow-lg">
        <div className="text-sm text-black/55">{form.items.length} item(s) · {fmt(total)}</div>
        <div className="flex gap-2">
          <Link to={`/t/${subdomain}/${listTo}`}><button className="px-4 py-2.5 rounded-xl border border-black/15 text-sm font-semibold">Cancel</button></Link>
          <PrimaryButton onClick={submit} disabled={busy} icon={Save} testid={`${testidBase}-submit`}>{busy ? "Saving…" : "Submit"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export const CreatePurchase = () => <TxForm title="Create Purchase" breadcrumb="Purchases · Create Purchase"
  listTo="purchases" mode="purchase" endpoint="/t/purchases" testidBase="cp"
  contactLabel="Supplier" contactEndpoint="/t/suppliers" priceField="unit_cost" />;

export const ImportPurchases = () => <PersonImport kind="Purchase" endpoint="/t/purchases/import" testidBase="ip" />;

// ═══════════════════════════════ SALES / PURCHASE RETURN ═══════════════════════════════
function ReturnsList({ title, breadcrumb, endpoint, testidBase, newTo, contactField }) {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);

  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.reference}?`)) return;
    await api.delete(`${endpoint}/${r.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8">
      <PageHeader title={title} breadcrumb={breadcrumb}
        actions={<Link to={`/t/${subdomain}/${newTo}`}><PrimaryButton icon={Plus} testid={`${testidBase}-new`}>Create Return</PrimaryButton></Link>} />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Reference</th>
              <th className="text-left p-3.5 font-bold">Date</th>
              <th className="text-left p-3.5 font-bold">{contactField === "customer_name" ? "Customer" : "Supplier"}</th>
              <th className="text-left p-3.5 font-bold">Items</th>
              <th className="text-left p-3.5 font-bold">Total</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-mono text-xs font-bold">{r.reference}</td>
                <td className="p-3.5 text-black/65 text-xs">{r.date}</td>
                <td className="p-3.5 font-semibold">{r[contactField]}</td>
                <td className="p-3.5 text-black/65">{(r.items || []).length}</td>
                <td className="p-3.5 font-bold tabular-nums text-rose-600">{fmt(r.total)}</td>
                <td className="p-3.5 text-right">
                  <button onClick={() => remove(r)} data-testid={`${testidBase}-del-${r.id}`}
                    className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-black/45">No returns yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export const AllSalesReturns = () => <ReturnsList title="All Sales Returns" breadcrumb="Sales Return · All"
  endpoint="/t/sales-returns" testidBase="sr" newTo="sales-return/new" contactField="customer_name" />;
export const CreateSalesReturn = () => <TxForm title="Create Sales Return" breadcrumb="Sales Return · Create"
  listTo="sales-return" mode="sales-return" endpoint="/t/sales-returns" testidBase="csr"
  contactLabel="Customer" contactEndpoint="/t/customers" />;
export const AllPurchaseReturns = () => <ReturnsList title="All Purchase Returns" breadcrumb="Purchase Return · All"
  endpoint="/t/purchase-returns" testidBase="pr" newTo="purchases-return/new" contactField="supplier_name" />;
export const CreatePurchaseReturn = () => <TxForm title="Create Purchase Return" breadcrumb="Purchase Return · Create"
  listTo="purchases-return" mode="purchase-return" endpoint="/t/purchase-returns" testidBase="cpr"
  contactLabel="Supplier" contactEndpoint="/t/suppliers" />;
